import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { NOT_RECORDED } from "../lib/counters";
import { avgText, keptWicketEver, toRateText } from "../lib/recordFigures";
import { leftOutNote } from "../lib/overs";

/**
 * Issue #1410 (D367.18, Martin 2026-10-17: "a single dashboard function"): what the Record
 * screen's "Show more" used to draw, now on the Dashboard (`/insights`) once.
 *
 * The union rule (Andreas, 2026-10-28): the Dashboard keeps every stat and graph either screen
 * had, and where both drew one it keeps the better-looking rendering. The pair table is in the
 * PR body. In short: the Dashboard's own tiles and its six compact charts win every pair
 * (cumulative runs, economy, batting average, strike rate, wickets), so none of those is drawn
 * twice. What only Record had is drawn here, in the Dashboard's idiom (the same card, the same
 * navy and light blue, two across on a phone): the charts by position, by season and by ground,
 * how you were out, cumulative wickets, the keeping split, the first-half to second-half
 * trends, and the milestones. The extra tiles live in `lib/careerFigures.js`.
 *
 * Every figure here reads `GET /analytics/career`, the payload `RecordDashboardScreen` already
 * fetched with the Dashboard's own filters; this component makes no request. An empty series
 * says so in one line rather than drawing an empty frame. Nothing here is a link or a button,
 * so the screen's count of places to go does not move. This screen has no gold element (R-003).
 */
const NAVY = "#0B2E59";
const BLUE = "#4A9BE0";
const PIE = [NAVY, BLUE, "#84C5FF", "#64748B", "#94A3B8", "#CBD5E1"];
const AXIS = { fontSize: 10, fill: "#64748B" };
const TT = { borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 };
const SIZE = { width: 150, height: 110 };
const WIDE_SIZE = { width: 300, height: 150 };

const cardStyle = {
  minWidth: 0,
  padding: "12px 8px 8px 10px",
  background: "var(--nw-surface)",
  border: "1px solid var(--nw-line)",
  borderRadius: "var(--nw-radius-card)",
};
const full = { gridColumn: "1 / -1" };
const arr = (v) => (Array.isArray(v) ? v : []);

/** A chart card: title, the picture (hidden from a screen reader), a caption, and the words that stand in for it. */
function ChartCard({ id, title, caption, summary, empty, wide = false, children }) {
  return (
    <section data-testid={`deeper-${id}`} aria-label={title} style={wide ? { ...cardStyle, ...full } : cardStyle}>
      <h3 className="nw-section" style={{ margin: "0 0 6px" }}>{title}</h3>
      {empty ? (
        <div className="nw-help" data-testid={`deeper-${id}-empty`} style={{ padding: "8px 0" }}>{empty}</div>
      ) : (
        <>
          <div aria-hidden="true">{children}</div>
          {caption && (
            <div className="nw-caption" data-testid={`deeper-${id}-caption`} style={{ color: "var(--nw-ink-muted)" }}>{caption}</div>
          )}
          <span className="sr-only">{summary}</span>
        </>
      )}
    </section>
  );
}

const count = (n, one) => `${n} ${one}${n === 1 ? "" : "s"}`;
const matchesOf = (n) => `${n} ${n === 1 ? "match" : "matches"}`;

function Bars({ data, x, y, color = NAVY, integer = false }) {
  return (
    <ResponsiveContainer width="100%" height={SIZE.height} initialDimension={SIZE}>
      <BarChart accessibilityLayer={false} data={data} margin={{ top: 6, right: 6, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
        <XAxis dataKey={x} tick={AXIS} interval="preserveStartEnd" />
        <YAxis tick={AXIS} width={30} allowDecimals={!integer} />
        <Tooltip contentStyle={TT} />
        <Bar dataKey={y} fill={color} radius={[4, 4, 0, 0]} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function HorizontalBars({ data, x, y }) {
  const height = Math.max(WIDE_SIZE.height, 28 * data.length + 24);
  return (
    <ResponsiveContainer width="100%" height={height} initialDimension={{ width: WIDE_SIZE.width, height }}>
      <BarChart accessibilityLayer={false} data={data} layout="vertical" margin={{ top: 6, right: 12, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
        <XAxis type="number" tick={AXIS} allowDecimals={false} />
        <YAxis dataKey={x} type="category" tick={AXIS} width={96} interval={0} />
        <Tooltip contentStyle={TT} />
        <Bar dataKey={y} fill={NAVY} radius={[0, 4, 4, 0]} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function Donut({ data, nameKey, valueKey }) {
  return (
    <ResponsiveContainer width="100%" height={WIDE_SIZE.height} initialDimension={WIDE_SIZE}>
      <PieChart accessibilityLayer={false}>
        <Pie rootTabIndex={-1} data={data} dataKey={valueKey} nameKey={nameKey} cx="50%" cy="50%" innerRadius={34} outerRadius={58} isAnimationActive={false}>
          {data.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
        </Pie>
        <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
        <Tooltip contentStyle={TT} />
      </PieChart>
    </ResponsiveContainer>
  );
}

/** The ground heat map: one tinted cell per ground, the darker the more runs. */
function GroundHeat({ rows }) {
  const max = Math.max(...rows.map((g) => g.runs), 1);
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 8 }}>
      {rows.map((g) => (
        <div key={g.ground} style={{ padding: 10, borderRadius: "var(--nw-radius-control)", background: `rgba(11, 46, 89, ${0.15 + (g.runs / max) * 0.75})` }}>
          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "rgba(255,255,255,0.75)" }}>{matchesOf(g.matches)}</div>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.ground}</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#fff", marginTop: 2 }}>{g.runs}</div>
        </div>
      ))}
    </div>
  );
}

/** Each match's own economy (`trend[].econ`), not the running figure the Dashboard's Economy chart draws. */
function EconomyEachMatch({ points }) {
  return (
    <ResponsiveContainer width="100%" height={SIZE.height} initialDimension={SIZE}>
      <LineChart accessibilityLayer={false} data={points} margin={{ top: 6, right: 6, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
        <XAxis dataKey="date" hide />
        <YAxis tick={AXIS} width={30} />
        <Tooltip contentStyle={TT} />
        <Line type="monotone" dataKey="econ" stroke={BLUE} strokeWidth={2.5} dot={{ r: 3, fill: NAVY }} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

function CumulativeWickets({ trend }) {
  return (
    <ResponsiveContainer width="100%" height={SIZE.height} initialDimension={SIZE}>
      <LineChart accessibilityLayer={false} data={trend} margin={{ top: 6, right: 6, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
        <XAxis dataKey="date" hide />
        <YAxis tick={AXIS} width={30} allowDecimals={false} />
        <Tooltip contentStyle={TT} />
        <Line type="monotone" dataKey="cum_wkts" stroke={NAVY} strokeWidth={2.5} dot={{ r: 3, fill: NAVY }} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

/** First half of the matches against the second: the two figures and the change between them. */
function HalfCard({ id, title, first, last, delta, improveIsUp, fmt, note }) {
  const hasData = first != null && last != null;
  const improved = improveIsUp ? delta > 0 : delta < 0;
  const flat = delta === 0;
  const Arrow = improved ? ArrowUpRight : ArrowDownRight;
  const verdict = flat ? "No change between the halves." : improved ? "Trending upward — keep the momentum." : "Slight decline — review recent form.";
  return (
    <section data-testid={`deeper-${id}`} aria-label={title} style={{ ...cardStyle, ...full }}>
      <h3 className="nw-section" style={{ margin: "0 0 6px" }}>{title}</h3>
      {!hasData ? (
        <div className="nw-help" data-testid={`deeper-${id}-empty`}>No trend between the halves yet.</div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8, textAlign: "center" }}>
            {[["1st half", fmt(first), "first"], ["2nd half", fmt(last), "last"], ["Change", fmt(Math.abs(delta)), "delta"]].map(([label, value, k]) => (
              <div key={k}>
                <div className="nw-caption" style={{ color: "var(--nw-ink-muted)" }}>{label}</div>
                <div data-testid={`deeper-${id}-${k}`} style={{ fontSize: 22, fontWeight: 800, color: "var(--nw-ink)", display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                  {k === "delta" && !flat && <Arrow size={16} aria-hidden="true" />}
                  {value}
                </div>
              </div>
            ))}
          </div>
          <div className="nw-help" data-testid={`deeper-${id}-verdict`} style={{ marginTop: 8 }}>{verdict}</div>
        </>
      )}
      {note && <div className="nw-help" data-testid={`deeper-${id}-left-out`} style={{ marginTop: 4 }}>Economy: {note}.</div>}
    </section>
  );
}

/** "2026-03-08" as "8 Mar 2026"; what cannot be read is shown as it came. */
function milestoneDate(iso) {
  if (!iso) return NOT_RECORDED;
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}

// #1700 G1 (D479): every chart here sits under `aria-hidden` (ChartCard), so each takes `accessibilityLayer={false}`:
// recharts' layer would otherwise make its svg a tab stop that nothing announces. The pie's own `g` is a tab stop the layer flag does not clear (measured), so it also takes `rootTabIndex={-1}`. The hover tooltip stays.
const groupStyle = { display: "flex", flexDirection: "column", gap: 8 };
const gridStyle = { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 };

export default function DeeperLook({ career }) {
  const batting = career?.batting || {};
  const bowling = career?.bowling || {};
  const wk = career?.wicketkeeping || null;
  const dev = career?.development || {};
  const trend = arr(career?.trend);
  const milestones = arr(career?.milestones);

  const byPosition = arr(batting.by_position);
  const out = arr(batting.dismissals).filter((d) => d.count > 0);
  const runsSeason = arr(batting.by_season);
  const runsGround = arr(batting.by_ground);
  const wktsSeason = arr(bowling.by_season);
  const wktsGround = arr(bowling.by_ground);
  const kept = keptWicketEver(wk);
  const keepSplit = [{ name: "Catches", value: wk?.catches ?? 0 }, { name: "Stumpings", value: wk?.stumpings ?? 0 }];
  const keepSplitDrawn = kept && keepSplit.some((k) => k.value > 0);
  // `econ` reads 0 both for a maiden spell and for a match with no bowling; only `bowled` tells them apart.
  const econPoints = trend.filter((p) => p.bowled === true);
  const hasWickets = trend.some((p) => (p.cum_wkts ?? 0) > 0);

  return (
    <>
      <div data-wide="full" data-testid="deeper-batting" style={groupStyle}>
        <div className="nw-section">Batting, in detail</div>
        <div style={gridStyle}>
          <ChartCard
            id="by-position"
            title="Average by position"
            empty={byPosition.length ? null : "No batting positions recorded yet."}
            caption="batting average, by batting position"
            summary={`Batting average by position: ${byPosition.map((p) => `${p.position}: ${avgText(p.avg)}`).join(", ")}.`}
          >
            <Bars data={byPosition} x="position" y="avg" />
          </ChartCard>
          <ChartCard
            id="runs-season"
            title="Runs by season"
            empty={runsSeason.length ? null : "No seasons yet."}
            caption={count(runsSeason.length, "season")}
            summary={`Runs by season: ${runsSeason.map((s) => `${s.season}: ${s.runs}`).join(", ")}.`}
          >
            <Bars data={runsSeason} x="season" y="runs" integer />
          </ChartCard>
          <ChartCard
            id="dismissals"
            title="How you were out"
            wide
            empty={out.length ? null : "No dismissals recorded yet."}
            summary={`How you were out: ${out.map((d) => `${d.type}: ${d.count}`).join(", ")}.`}
          >
            <Donut data={out} nameKey="type" valueKey="count" />
          </ChartCard>
          <ChartCard
            id="runs-ground"
            title="Runs by ground"
            wide
            empty={runsGround.length ? null : "No grounds yet."}
            caption="the darker, the more runs"
            summary={`Runs by ground: ${runsGround.map((g) => `${g.ground}: ${g.runs}`).join(", ")}.`}
          >
            <GroundHeat rows={runsGround} />
          </ChartCard>
        </div>
      </div>

      <div data-wide="full" data-testid="deeper-bowling" style={groupStyle}>
        <div className="nw-section">Bowling, in detail</div>
        <div style={gridStyle}>
          <ChartCard
            id="wickets-season"
            title="Wickets by season"
            empty={wktsSeason.length ? null : "No seasons yet."}
            caption={count(wktsSeason.length, "season")}
            summary={`Wickets by season: ${wktsSeason.map((s) => `${s.season}: ${s.wickets}`).join(", ")}.`}
          >
            <Bars data={wktsSeason} x="season" y="wickets" integer />
          </ChartCard>
          <ChartCard
            id="cum-wickets"
            title="Cumulative wickets"
            empty={hasWickets ? null : "No wickets yet."}
            caption="a line, every match"
            summary={`Cumulative wickets: ${trend.length ? trend[trend.length - 1].cum_wkts : 0} over ${matchesOf(trend.length)}.`}
          >
            <CumulativeWickets trend={trend} />
          </ChartCard>
          <ChartCard
            id="econ-match"
            title="Economy in each match"
            empty={econPoints.length ? null : "No bowling yet."}
            caption="each match on its own, not the running figure"
            summary={`Economy in each match: ${econPoints.map((p) => toRateText(p.econ)).join(", ")}.`}
          >
            <EconomyEachMatch points={econPoints} />
          </ChartCard>
          <ChartCard
            id="wickets-ground"
            title="Wickets by ground"
            wide
            empty={wktsGround.length ? null : "No grounds yet."}
            summary={`Wickets by ground: ${wktsGround.map((g) => `${g.ground}: ${g.wickets}`).join(", ")}.`}
          >
            <HorizontalBars data={wktsGround} x="ground" y="wickets" />
          </ChartCard>
        </div>
      </div>

      <div data-wide="full" data-testid="deeper-keeping" style={groupStyle}>
        <div className="nw-section">Wicket-keeping, in detail</div>
        <div style={gridStyle}>
          <ChartCard
            id="keeping-split"
            title="Catches and stumpings"
            wide
            empty={keepSplitDrawn ? null : kept ? "No catches or stumpings yet." : "A dash, not a nought: you haven't kept wicket."}
            summary={`Catches ${keepSplit[0].value}, stumpings ${keepSplit[1].value}.`}
          >
            <Donut data={keepSplit} nameKey="name" valueKey="value" />
          </ChartCard>
        </div>
      </div>

      <div data-wide="full" data-testid="deeper-trend" style={groupStyle}>
        <div className="nw-section">How you&#x27;re trending</div>
        <div style={gridStyle}>
          <HalfCard
            id="trend-batting"
            title="Batting average, first half to second half"
            first={dev.batting_first_avg}
            last={dev.batting_last_avg}
            delta={dev.batting_delta}
            improveIsUp
            fmt={avgText}
          />
          <HalfCard
            id="trend-economy"
            title="Economy, first half to second half"
            first={dev.econ_first}
            last={dev.econ_last}
            delta={dev.econ_delta}
            improveIsUp={false}
            fmt={toRateText}
            note={leftOutNote(bowling.overs_left_out)}
          />
        </div>
      </div>

      <div data-wide="full" data-testid="deeper-milestones" style={groupStyle}>
        <div className="nw-section">Milestones</div>
        {milestones.length === 0 ? (
          <div className="nw-help" data-testid="deeper-milestones-empty">No milestones yet — keep going!</div>
        ) : (
          <div style={{ ...cardStyle, padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
            {milestones.map((m, i) => (
              <div key={i} data-testid="deeper-milestone" className="nw-prose">
                <strong>{m.type}</strong>: {m.value}
                {m.ground ? ` at ${m.ground}` : ""} — {milestoneDate(m.date)}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
